import { useState, useEffect, useCallback, useRef } from 'react';
import type { ColumnDefinition } from '@/components/column-configurator-modal';
import { getDataMode, fetchColumnPreferences, saveColumnPreferences } from '@/lib/data-service';

/**
 * Column IDs that must never be visible in the project list grid.
 * These columns were deprecated but may still exist in users' stored preferences
 * with `visible: true`. The merge logic forces them to `visible: false`.
 */
export const FORCE_HIDDEN_COLUMN_IDS: ReadonlySet<string> = new Set([
  'phase',
  'next-milestone',
  'milestones-completed',
  'overdue-milestones',
  'tasks-overdue',
  'risk-level',
  'quality-score',
]);

interface UseColumnPreferencesOptions {
  storageKey: string;
  defaultColumns: ColumnDefinition[];
}

interface UseColumnPreferencesReturn {
  columns: ColumnDefinition[];
  updateColumns: (newColumns: ColumnDefinition[]) => void;
  resetColumns: () => void;
  isLoading: boolean;
}

export function mergeColumnsWithDefaults(
  storedColumns: ColumnDefinition[],
  defaultColumns: ColumnDefinition[]
): ColumnDefinition[] {
  const mergedColumns = defaultColumns.map((defaultCol) => {
    const storedCol = storedColumns.find((col) => col.id === defaultCol.id);
    const merged = storedCol ? { ...defaultCol, ...storedCol } : { ...defaultCol };

    if (FORCE_HIDDEN_COLUMN_IDS.has(merged.id)) {
      merged.visible = false;
    }

    if (defaultCol.hidden) {
      merged.hidden = true;
      merged.visible = false;
    }

    return merged;
  });

  return mergedColumns.sort((a, b) => a.order - b.order);
}

/**
 * Hook for managing column preferences with database persistence (live mode)
 * or localStorage persistence (demo mode)
 * 
 * @param options - Configuration options
 * @param options.storageKey - Unique key for localStorage (used in demo mode and as fallback)
 * @param options.defaultColumns - Default column configuration
 * @returns Column preferences state and update functions
 */
export function useColumnPreferences({
  storageKey,
  defaultColumns,
}: UseColumnPreferencesOptions): UseColumnPreferencesReturn {
  const [columns, setColumns] = useState<ColumnDefinition[]>(defaultColumns);
  const [isLoading, setIsLoading] = useState(true);
  const isSavingRef = useRef(false);

  useEffect(() => {
    async function loadPreferences() {
      const mode = getDataMode();

      // In live mode, try to fetch from API first (uses /userpreferences/me endpoint)
      if (mode === 'live') {
        try {
          const apiColumns = await fetchColumnPreferences(storageKey);
          if (apiColumns && apiColumns.length > 0) {
            const merged = mergeColumnsWithDefaults(apiColumns as ColumnDefinition[], defaultColumns);
            setColumns(merged);
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('Failed to load column preferences from API:', error);
        }
      }

      // Fallback to localStorage (demo mode or API failure)
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsedColumns = JSON.parse(stored) as ColumnDefinition[];
          const merged = mergeColumnsWithDefaults(parsedColumns, defaultColumns);
          setColumns(merged);
        } else {
          setColumns(defaultColumns);
        }
      } catch (error) {
        console.error('Failed to load column preferences from localStorage:', error);
        setColumns(defaultColumns);
      } finally {
        setIsLoading(false);
      }
    }

    loadPreferences();
  }, [storageKey, defaultColumns]);

  const updateColumns = useCallback(
    async (newColumns: ColumnDefinition[]) => {
      setColumns(newColumns);

      // Always save to localStorage as fallback
      localStorage.setItem(storageKey, JSON.stringify(newColumns));

      const mode = getDataMode();

      console.log('[useColumnPreferences] updateColumns called:', {
        mode,
        isSaving: isSavingRef.current,
        willCallApi: mode === 'live' && !isSavingRef.current,
      });

      // In live mode, save to API (uses /userpreferences/me endpoint - no userId needed)
      if (mode === 'live' && !isSavingRef.current) {
        isSavingRef.current = true;
        console.log('[useColumnPreferences] Making API call to save column preferences');
        try {
          await saveColumnPreferences(newColumns, storageKey);
          console.log('[useColumnPreferences] Successfully saved column preferences to API');
        } catch (error) {
          console.error('[useColumnPreferences] Failed to save column preferences to API:', error);
        } finally {
          isSavingRef.current = false;
        }
      } else if (mode !== 'live') {
        console.log('[useColumnPreferences] Skipping API call - not in live mode');
      }
    },
    [storageKey]
  );

  const resetColumns = useCallback(async () => {
    setColumns(defaultColumns);
    localStorage.removeItem(storageKey);

    const mode = getDataMode();

    // In live mode, reset in API as well
    if (mode === 'live' && !isSavingRef.current) {
      isSavingRef.current = true;
      try {
        await saveColumnPreferences(defaultColumns, storageKey);
      } catch (error) {
        console.error('Failed to reset column preferences in API:', error);
      } finally {
        isSavingRef.current = false;
      }
    }
  }, [storageKey, defaultColumns]);

  return {
    columns,
    updateColumns,
    resetColumns,
    isLoading,
  };
}
